/**
 * Cliente leve para os overrides de runtime guardados em `site_config`.
 *
 * Hidrata uma vez via GET /api/site-config e expõe um getter síncrono.
 * Cache em memória + revalidação a cada 60s.
 */

type OverrideMap = Record<string, unknown>;

let _cache: OverrideMap = {};
let _loadedAt = 0;
let _inFlight: Promise<void> | null = null;
const TTL_MS = 60_000;

async function fetchOnce(): Promise<void> {
  if (_inFlight) return _inFlight;
  _inFlight = (async () => {
    try {
      const res = await fetch("/api/site-config", { cache: "no-store" });
      if (res.ok) {
        const json = (await res.json()) as OverrideMap;
        _cache = json || {};
        _loadedAt = Date.now();
      }
    } catch {
      // silencia — fallback fica nos defaults
    } finally {
      _inFlight = null;
    }
  })();
  return _inFlight;
}

/** Carrega overrides se ainda não carregou ou se passou do TTL. */
export async function ensureOverridesLoaded(): Promise<void> {
  if (Date.now() - _loadedAt < TTL_MS) return;
  await fetchOnce();
}

/** Leitura síncrona — pode retornar undefined antes do hydration. */
export function getOverride<T = unknown>(key: string): T | undefined {
  return _cache[key] as T | undefined;
}

/** Força refresh (use após aplicar uma ação no admin). */
export async function refreshOverrides(): Promise<void> {
  _loadedAt = 0;
  await fetchOnce();
}

/** Hydration "best-effort" no boot do cliente. */
if (typeof window !== "undefined") {
  void ensureOverridesLoaded();
}
